import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const ThemeContext = createContext({ settings: null, refresh: async () => {} });

/**
 * Loads site-wide branding (theme colours, typography, logo, favicon,
 * meta, footer) from `/api/settings/public` and applies them at runtime:
 *
 *  - Injects `--brand-*` CSS variables on :root so any Tailwind arbitrary
 *    value like `text-[var(--brand-primary)]` picks up the current brand.
 *  - Loads Google Fonts dynamically via a <link> tag.
 *  - Updates `<title>` and `<link rel="icon">` from the CMS.
 *
 * Super Admin changes to the settings collection are reflected on the
 * public site after the next page load (or by calling `refresh()`).
 */
export const ThemeProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/settings/public`);
      setSettings(data);
      applySettings(data);
    } catch (e) {
      console.warn("ThemeProvider load failed", e?.message);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <ThemeContext.Provider value={{ settings, refresh: load }}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

function applySettings(s) {
  if (!s) return;
  // 1) CSS variables ---------------------------------------------------
  const t = s.theme || {};
  const typo = s.typography || {};
  const root = document.documentElement;
  // Auto-compute a foreground token for the primary color so buttons stay
  // legible when Super Admin picks a dark/light custom primary.
  const primary = t.primary || "#b8860b";
  const fg = luminance(primary) > 0.55 ? "#0a0a0a" : "#ffffff";
  Object.entries({
    "--brand-primary": t.primary,
    "--brand-primary-foreground": fg,
    "--brand-accent":  t.accent,
    "--brand-dark":    t.dark,
    "--brand-background": t.background,
    "--brand-text":    t.text,
    "--brand-muted":   t.muted,
    "--brand-border":  t.border,
    "--brand-heading-font": typo.heading_font ? `'${typo.heading_font}', serif` : undefined,
    "--brand-body-font":    typo.body_font    ? `'${typo.body_font}', sans-serif`    : undefined,
    "--brand-heading-weight": typo.heading_weight,
    "--brand-body-weight":    typo.body_weight,
  }).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") root.style.setProperty(k, String(v)); });

  // 2) Google Fonts ----------------------------------------------------
  const fonts = [typo.heading_font, typo.body_font].filter(Boolean);
  if (fonts.length) {
    const family = fonts.map((f) => `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@300;400;500;600;700`).join("&");
    const href = `https://fonts.googleapis.com/css2?${family}&display=swap`;
    let link = document.getElementById("adelina-google-fonts");
    if (!link) {
      link = document.createElement("link");
      link.id = "adelina-google-fonts";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
    // apply body font family
    document.body.style.fontFamily = `'${typo.body_font || "Manrope"}', system-ui, sans-serif`;
  }

  // 3) Favicon + document title ---------------------------------------
  if (s.favicon_url) {
    let icon = document.querySelector("link[rel~='icon']");
    if (!icon) {
      icon = document.createElement("link");
      icon.rel = "icon";
      document.head.appendChild(icon);
    }
    icon.href = s.favicon_url.startsWith("http") ? s.favicon_url : `${BACKEND}${s.favicon_url}`;
  }
  const seo = s.seo || {};
  const seoDefault = seo.default || {};
  const title = seoDefault.title || s.meta?.title;
  if (title) document.title = title;

  // 4) Meta description / keywords ------------------------------------
  const setMeta = (name, content, attr = "name") => {
    if (!content) return;
    let m = document.querySelector(`meta[${attr}="${name}"]`);
    if (!m) { m = document.createElement("meta"); m.setAttribute(attr, name); document.head.appendChild(m); }
    m.setAttribute("content", content);
  };
  setMeta("description", seoDefault.description || s.meta?.description);
  setMeta("keywords",    seoDefault.keywords    || s.meta?.keywords);

  // 5) Open Graph + Twitter Card tags ---------------------------------
  const ogImage = seoDefault.og_image;
  setMeta("og:title", title, "property");
  setMeta("og:description", seoDefault.description || s.meta?.description, "property");
  setMeta("og:type", "website", "property");
  if (seo.site_url) setMeta("og:url", seo.site_url, "property");
  if (ogImage)      setMeta("og:image", ogImage, "property");
  setMeta("twitter:card", seoDefault.twitter_card || "summary_large_image");
  setMeta("twitter:title", title);
  setMeta("twitter:description", seoDefault.description || s.meta?.description);
  if (ogImage) setMeta("twitter:image", ogImage);
  if (seoDefault.twitter_site) setMeta("twitter:site", seoDefault.twitter_site);

  // 6) Canonical link -------------------------------------------------
  const canonical = seoDefault.canonical || (seo.site_url ? `${seo.site_url.replace(/\/$/, "")}${window.location.pathname}` : "");
  if (canonical) {
    let link = document.querySelector("link[rel='canonical']");
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = canonical;
  }

  // 7) Analytics scripts ---------------------------------------------
  injectAnalytics(seo.analytics || {});
}

// Idempotently insert GA4 / GTM / Meta Pixel / custom head+body scripts.
function injectAnalytics(a) {
  // SECURITY NOTE: `html` here is analytics/tracking scripts (Google Analytics,
  // Meta Pixel, Hotjar…) set by a Super Admin in the CMS settings. Sanitising
  // would strip the <script> tags that are the entire point of the feature.
  // Trust boundary: only Super Admins can write to `settings.analytics.*`;
  // regular admins are gated by RBAC.
  const injectOnce = (id, html, where = "head") => {
    if (!html || document.getElementById(id)) return;
    const container = document.createElement("div");
    container.id = id;
    container.innerHTML = html; // SECURITY: analytics HTML from trusted Super-Admin CMS settings; scripts must execute.
    // Move <script> children so they actually execute
    Array.from(container.querySelectorAll("script")).forEach((old) => {
      const s = document.createElement("script");
      for (const attr of old.attributes) s.setAttribute(attr.name, attr.value);
      s.text = old.text;
      (where === "body" ? document.body : document.head).appendChild(s);
    });
    (where === "body" ? document.body : document.head).appendChild(container);
  };

  if (a.ga4_id) {
    injectOnce("adelina-ga4", `
      <script async src="https://www.googletagmanager.com/gtag/js?id=${a.ga4_id}"></script>
      <script>
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${a.ga4_id}');
      </script>
    `);
  }
  if (a.gtm_id) {
    injectOnce("adelina-gtm", `
      <script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${a.gtm_id}');</script>
    `);
  }
  if (a.meta_pixel_id) {
    injectOnce("adelina-fbq", `
      <script>!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
      document,'script','https://connect.facebook.net/en_US/fbevents.js');
      fbq('init','${a.meta_pixel_id}');fbq('track','PageView');</script>
    `);
  }
  if (a.custom_head) injectOnce("adelina-custom-head", a.custom_head, "head");
  if (a.custom_body) injectOnce("adelina-custom-body", a.custom_body, "body");
}

// WCAG-style relative luminance for a hex color (0..1). Used to auto-select
// a legible foreground when the admin picks a custom primary color.
function luminance(hex) {
  const m = /^#?([a-f0-9]{6})$/i.exec((hex || "").trim());
  if (!m) return 0.5;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255);
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
